// Phase 0093-B — Auto-fetch summary capture mode.
//
// Purpose-built for studies where the ERP already has most of the data
// (Manpower Requirement, Absenteeism, Labour Productivity, Grievance
// Pattern). The screen renders:
//
//   * Large KPI cards driven by ``summary_cards`` from the server.
//   * A primary-metric header tile (with status colour).
//   * Source footer driven by ``source_status`` + ``missing_sources``.
//   * Calculation-confidence chip (EXACT / ESTIMATED / MANUAL / DATA_NOT_AVAILABLE).
//   * "Data not available" banner if the primary source is missing.
//   * Chip groups for the operator's reason/action picks.
//   * Optional remarks textarea.
//   * Quick-Mode toggle (default: ON) which collapses summary cards into
//     a single primary tile and hides the source footer; Detailed Mode
//     shows everything for IE engineers.
//   * One Submit button → POST submit endpoint → server computes result
//     and stamps calculation_confidence.
//
// The frontend never computes the final business result — it only
// captures chip selections and submits them.

import { useEffect, useMemo, useState } from 'react'
import {
  Alert, App, Badge, Button, Card, Col, Empty, Result, Row,
  Segmented, Skeleton, Space, Switch, Tag, Tooltip, Typography,
} from 'antd'
import { CheckCircleOutlined, ExclamationCircleOutlined, InfoCircleOutlined, LeftOutlined, ReloadOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'

import { liveStudyApi, type AutoFetchSummary, type AutoFetchSummaryCard } from '@/features/hr/liveStudyApi'

const { Text, Title } = Typography

// Status → Ant Design colour + label
const STATUS_PRESETS: Record<string, { color: string; label: string }> = {
  OK: { color: 'success', label: 'OK' },
  INFO: { color: 'default', label: 'Info' },
  WARN: { color: 'warning', label: 'Watch' },
  ALERT: { color: 'error', label: 'Alert' },
}

const CONFIDENCE_PRESETS: Record<string, { color: string; tip: string }> = {
  EXACT: {
    color: 'green',
    tip: 'All primary sources present. Numbers computed directly from data.',
  },
  ESTIMATED: {
    color: 'gold',
    tip: 'Some sources missing. Numbers derived from secondary or partial data.',
  },
  MANUAL: {
    color: 'blue',
    tip: 'No automated source. Result based on the supervisor\'s manual selection.',
  },
  DATA_NOT_AVAILABLE: {
    color: 'red',
    tip: 'Primary data source is missing. Submission will be marked DATA_NOT_AVAILABLE.',
  },
}

function KpiCard({ card }: { card: AutoFetchSummaryCard }) {
  const preset = STATUS_PRESETS[card.status] ?? STATUS_PRESETS.INFO
  const isNull = card.value === null || card.value === undefined
  return (
    <Card
      size="small"
      style={{
        minHeight: 96,
        borderColor: preset.color === 'error' ? '#ff4d4f'
                    : preset.color === 'warning' ? '#faad14'
                    : preset.color === 'success' ? '#52c41a'
                    : '#d9d9d9',
        borderWidth: 1.5,
      }}
    >
      <Space direction="vertical" size={0} style={{ width: '100%' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>{card.label}</Text>
        <Space size={4} align="baseline">
          <span style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.1 }}>
            {isNull ? '—' : card.value}
          </span>
          {card.unit && (
            <Text type="secondary" style={{ fontSize: 13 }}>{card.unit}</Text>
          )}
        </Space>
      </Space>
    </Card>
  )
}

export default function AutoFetchSummaryCapture() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { message } = App.useApp()
  const [quickMode, setQuickMode] = useState(true)
  const [chips, setChips] = useState<Record<string, string>>({})
  const [manualFields, setManualFields] = useState<Record<string, string>>({})
  const [remarks, setRemarks] = useState('')

  const { data: summary, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['auto-fetch-summary', sessionId],
    queryFn: () => liveStudyApi.autoFetchSummary(sessionId!),
    enabled: !!sessionId,
  })

  const { data: bundle } = useQuery({
    queryKey: ['study-session', sessionId],
    queryFn: () => liveStudyApi.getSession(sessionId!),
    enabled: !!sessionId,
  })

  const submitMut = useMutation({
    mutationFn: () => liveStudyApi.submitAutoFetchSummary(sessionId!, {
      chip_selections: chips,
      manual_fields: manualFields,
      remarks: remarks || undefined,
    }),
    onSuccess: () => {
      message.success('Study submitted. Result computed.')
      qc.invalidateQueries({ queryKey: ['study-session', sessionId] })
      qc.invalidateQueries({ queryKey: ['auto-fetch-summary', sessionId] })
    },
    onError: (e: Error) => message.error(e.message),
  })

  const sess = bundle?.session
  const tmpl = bundle?.template
  const isCompleted = sess && ['COMPLETED', 'VERIFIED'].includes(sess.session_status)

  const confidence = summary?.calculation_confidence ?? 'DATA_NOT_AVAILABLE'
  const recStatus = summary?.recommended_result_status ?? 'DATA_NOT_AVAILABLE'
  const conf = CONFIDENCE_PRESETS[confidence] ?? CONFIDENCE_PRESETS.DATA_NOT_AVAILABLE

  // Validation — every "manual_fields_required" chip group must have a pick
  const missingChips = useMemo(() => {
    if (!summary) return []
    const required = summary.manual_fields_required ?? []
    return required.filter((code) => !chips[code] && !manualFields[code])
  }, [summary, chips, manualFields])

  const canSubmit = !isCompleted && !!summary && missingChips.length === 0
    && confidence !== 'DATA_NOT_AVAILABLE' || (confidence === 'MANUAL' && missingChips.length === 0)

  useEffect(() => {
    // Reset chips when session changes
    if (sessionId) {
      setChips({})
      setManualFields({})
      setRemarks('')
    }
  }, [sessionId])

  if (isLoading) {
    return <Card style={{ margin: 16 }}><Skeleton active /></Card>
  }
  if (!summary) {
    return (
      <Result
        status="404"
        title="Study session not found"
        extra={
          <Button onClick={() => navigate('/hr/live-studies')}>
            <LeftOutlined /> Back
          </Button>
        }
      />
    )
  }

  return (
    <div style={{ padding: 16, maxWidth: 1200, margin: '0 auto' }}>
      {/* Top bar */}
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}>
        <Button type="text" icon={<LeftOutlined />}
                onClick={() => navigate('/hr/live-studies')}>
          Back
        </Button>
        <Space>
          <Text type="secondary" style={{ fontSize: 12 }}>Mode</Text>
          <Segmented
            value={quickMode ? 'quick' : 'detailed'}
            onChange={(v) => setQuickMode(v === 'quick')}
            options={[
              { label: 'Quick', value: 'quick' },
              { label: 'Detailed', value: 'detailed' },
            ]}
            size="small"
          />
          <Button icon={<ReloadOutlined />} size="small"
                  loading={isFetching}
                  onClick={() => refetch()}>
            Refresh
          </Button>
        </Space>
      </Space>

      {/* Title + confidence chip */}
      <Title level={3} style={{ marginTop: 0 }}>
        {tmpl?.template_name ?? summary.study_code}{' '}
        <Tooltip title={conf.tip}>
          <Tag color={conf.color} style={{ marginLeft: 8 }}>
            {confidence.replace(/_/g, ' ')}
          </Tag>
        </Tooltip>
      </Title>

      {/* DATA_NOT_AVAILABLE banner */}
      {confidence === 'DATA_NOT_AVAILABLE' && (
        <Alert
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          message="Data not available"
          description={
            summary.missing_sources.length
              ? summary.missing_sources.join(' · ')
              : 'Primary data sources for this study are not configured yet.'
          }
          style={{ marginBottom: 16 }}
        />
      )}
      {confidence === 'MANUAL' && (
        <Alert
          type="info"
          showIcon
          message="Manual observation mode"
          description={
            summary.missing_sources.length
              ? summary.missing_sources.join(' · ')
              : 'Source data is unavailable. Fill the chips below to log a manual observation.'
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Primary metric tile */}
      {summary.primary_metric.value !== null && (
        <Card style={{
          marginBottom: 16,
          background: recStatus === 'GAP_FOUND' ? '#fff1f0'
                     : recStatus === 'ON_TARGET' ? '#f6ffed'
                     : '#fafafa',
          borderColor: recStatus === 'GAP_FOUND' ? '#ff4d4f'
                       : recStatus === 'ON_TARGET' ? '#52c41a'
                       : '#d9d9d9',
        }}>
          <Row align="middle" gutter={16}>
            <Col flex="auto">
              <Text type="secondary">{summary.primary_metric.label}</Text>
              <div>
                <span style={{ fontSize: 48, fontWeight: 700 }}>
                  {summary.primary_metric.value}
                </span>
                <Text style={{ fontSize: 18, marginLeft: 8 }}>
                  {summary.primary_metric.unit}
                </Text>
              </div>
              {summary.primary_metric.target !== null
                && summary.primary_metric.target !== undefined && (
                <Text type="secondary">
                  Target: {summary.primary_metric.target} {summary.primary_metric.unit}
                </Text>
              )}
            </Col>
            <Col>
              {recStatus === 'GAP_FOUND' ? (
                <Tag color="error" style={{ fontSize: 16, padding: '6px 14px' }}>
                  <ExclamationCircleOutlined /> GAP FOUND
                </Tag>
              ) : recStatus === 'ON_TARGET' ? (
                <Tag color="success" style={{ fontSize: 16, padding: '6px 14px' }}>
                  <CheckCircleOutlined /> ON TARGET
                </Tag>
              ) : (
                <Tag style={{ fontSize: 14, padding: '6px 14px' }}>
                  <InfoCircleOutlined /> DATA NOT AVAILABLE
                </Tag>
              )}
            </Col>
          </Row>
        </Card>
      )}

      {/* Summary cards (Detailed mode only) */}
      {!quickMode && summary.summary_cards.length > 0 && (
        <>
          <Title level={5}>Breakdown</Title>
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            {summary.summary_cards.map((card, idx) => (
              <Col key={idx} xs={12} sm={8} md={6}>
                <KpiCard card={card} />
              </Col>
            ))}
          </Row>
        </>
      )}

      {/* Chip groups */}
      {summary.chip_groups.length > 0 && (
        <Card title="Reason / Action" style={{ marginBottom: 16 }} size="small">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {summary.chip_groups.map((group) => {
              const required = summary.manual_fields_required.includes(group.code)
              const selected = chips[group.code]
              return (
                <div key={group.code}>
                  <Text strong>
                    {group.label}
                    {required && <Text type="danger"> *</Text>}
                  </Text>
                  <div style={{ marginTop: 8 }}>
                    <Space wrap>
                      {group.options.map((opt) => (
                        <Tag.CheckableTag
                          key={opt}
                          checked={selected === opt}
                          onChange={(checked) => setChips((prev) => ({
                            ...prev,
                            [group.code]: checked ? opt : '',
                          }))}
                          style={{
                            padding: '6px 14px',
                            fontSize: 14,
                            borderRadius: 16,
                            border: '1px solid',
                            borderColor: selected === opt ? '#1677ff' : '#d9d9d9',
                          }}
                        >
                          {opt.replace(/_/g, ' ')}
                        </Tag.CheckableTag>
                      ))}
                    </Space>
                  </div>
                </div>
              )
            })}
          </Space>
        </Card>
      )}

      {/* CAPA preview */}
      {summary.capa_recommendation && (
        <Alert
          type={recStatus === 'GAP_FOUND' ? 'warning' : 'info'}
          showIcon
          message={`CAPA pre-filled: ${summary.capa_recommendation.type}`}
          description={
            <>
              <Text>{summary.capa_recommendation.text}</Text>
              <br />
              <Text type="secondary">
                Owner role: <Tag>{summary.capa_recommendation.owner_role}</Tag>
              </Text>
            </>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Remarks */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Text>Remarks (optional)</Text>
        <textarea
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          rows={2}
          maxLength={500}
          placeholder="Anything else the supervisor should note"
          style={{
            width: '100%', marginTop: 6, padding: 8,
            border: '1px solid #d9d9d9', borderRadius: 6,
          }}
        />
      </Card>

      {/* Source footer (Detailed mode only) */}
      {!quickMode && Object.keys(summary.source_status).length > 0 && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>SOURCES</Text>
          <Space wrap style={{ marginTop: 8 }}>
            {Object.entries(summary.source_status).map(([src, status]) => (
              <Badge
                key={src}
                status={status === 'OK' ? 'success' : 'error'}
                text={`${src}: ${status}`}
              />
            ))}
          </Space>
        </Card>
      )}

      {/* Submit */}
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <div>
          {missingChips.length > 0 && (
            <Text type="danger">
              Please select: {missingChips.map((c) => c.replace(/_/g, ' ')).join(', ')}
            </Text>
          )}
        </div>
        <Button
          type="primary"
          size="large"
          disabled={!canSubmit || submitMut.isPending}
          loading={submitMut.isPending}
          onClick={() => submitMut.mutate()}
        >
          {isCompleted ? 'Submitted' : 'Submit Study'}
        </Button>
      </Space>

      {/* Already-submitted summary tail */}
      {isCompleted && bundle?.result && (
        <Card style={{ marginTop: 16 }} size="small">
          <Text type="secondary">Result snapshot</Text>
          <div>
            <Tag color={bundle.result.status === 'ON_TARGET' ? 'success'
                       : bundle.result.status === 'BELOW_STANDARD' ? 'error'
                       : 'default'}>
              {bundle.result.status ?? 'N/A'}
            </Tag>
            <Tag>{bundle.result.calculation_confidence ?? confidence}</Tag>
          </div>
        </Card>
      )}

      {summary.summary_cards.length === 0
        && summary.chip_groups.length === 0
        && confidence === 'DATA_NOT_AVAILABLE' && (
        <Empty
          description="No data sources are configured for this study yet."
          style={{ marginTop: 32 }}
        />
      )}
    </div>
  )
}
